import { useCallback, useMemo, type ReactNode } from "react"
import { ListChecks } from "@phosphor-icons/react"

import ColumnChooserCore from "./ColumnChooserCore"
import { useChooserColumns } from "./useChooserColumns"

export type ColumnChooserColumnMetadata = {
  id: string
  displayName?: string
  description?: string
  group: string
  keyWords?: string[]
}

type Props = {
  columnIds: string[]
  displayedColumnIds: string[]
  setDisplayedColumnIds: (ids: string[]) => void

  columnMetadata: ColumnChooserColumnMetadata[]
  /** The table's own labels, which win over the naming registry. */
  getColumnLabel?: (columnId: string) => string | undefined
  fallbackColumnTitles?: Record<string, string>
  /** Formats an id nothing else names; defaults to splitting the camelCase id. */
  formatColumnName?: (columnId: string) => string
  getGroupIcon?: (group: string) => ReactNode
  otherGroupLabel?: string

  nonRemovableColumns?: string[]
  defaultColumnIds?: readonly string[]
  onResetToDefaultApplied?: () => void

  trackingPrefix: string
  tableId: string
  title?: string
}

/** A table's own column copy and grouping, as the chooser renders it. */
export type ColumnChooserCopy = Pick<
  Props,
  | "columnMetadata"
  | "getColumnLabel"
  | "getGroupIcon"
  | "otherGroupLabel"
  | "formatColumnName"
  | "trackingPrefix"
>

const defaultGroupIcon = <ListChecks size={16} className="text-fg-muted" />

/** {@link ColumnChooserCore} for tables that describe their columns with `columnMetadata`. */
export default function ColumnChooserDialog({
  columnIds,
  displayedColumnIds,
  setDisplayedColumnIds,
  columnMetadata,
  getColumnLabel,
  fallbackColumnTitles,
  formatColumnName,
  getGroupIcon,
  otherGroupLabel = "Other",
  nonRemovableColumns,
  defaultColumnIds,
  onResetToDefaultApplied,
  trackingPrefix,
  tableId,
  title,
}: Props) {
  const metadataNames = useMemo(
    () =>
      new Map(
        columnMetadata.flatMap(({ id, displayName }) =>
          displayName ? [[id, displayName] as const] : [],
        ),
      ),
    [columnMetadata],
  )
  const overrides = useCallback(
    (id: string) => metadataNames.get(id) ?? getColumnLabel?.(id),
    [metadataNames, getColumnLabel],
  )
  const titles = useMemo(
    () => ({ overrides, fallbacks: fallbackColumnTitles, format: formatColumnName }),
    [overrides, fallbackColumnTitles, formatColumnName],
  )

  const columns = useChooserColumns({
    columnIds,
    curated: columnMetadata,
    titles,
    otherGroup: otherGroupLabel,
    excludedIds: nonRemovableColumns,
  })

  return (
    <ColumnChooserCore
      columns={columns}
      columnIds={columnIds}
      displayedColumnIds={displayedColumnIds}
      setDisplayedColumnIds={setDisplayedColumnIds}
      defaultColumnIds={defaultColumnIds}
      onResetToDefaultApplied={onResetToDefaultApplied}
      getGroupIcon={(group) => getGroupIcon?.(group) ?? defaultGroupIcon}
      trackingPrefix={trackingPrefix}
      tableId={tableId}
      triggerLabel="Columns"
      title={title}
    />
  )
}
